import { 
  collection, 
  query, 
  where, 
  Timestamp, 
  addDoc,
  orderBy,
  onSnapshot,
  getDocs,
  doc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../config';
import { executeQuery } from './base';
import type { Appointment } from '@/types';

export type { Appointment };

interface AppointmentData {
  customerId: string;
  coachId: string;
  date: Date;
  status: string;
  customerPhone?: string;
  customerName?: string;
  customerNameEn?: string;
  customerNameAr?: string;
}

async function isWithinScheduledAvailability(coachId: string, slot: Date): Promise<boolean> {
  try {
    // Get all approved availabilities for the coach
    const availabilitiesQuery = query(
      collection(db, 'availabilities'),
      where('coachId', '==', coachId),
      where('status', '==', 'approved')
    );
    const snapshot = await getDocs(availabilitiesQuery);
    
    if (snapshot.empty) {
      return false; // No approved availabilities
    }
    
    const availabilities = snapshot.docs.map(doc => doc.data());
    
    // Check if the slot falls within any approved availability
    for (const availability of availabilities) {
      const startDate = availability.startDate.toDate();
      const endDate = availability.endDate.toDate();
      
      // Check if slot is within the date range
      if (slot >= startDate && slot <= endDate) {
        // Check if slot is within the scheduled days and times
        const dayOfWeek = slot.getDay().toString();
        const selectedDays = availability.selectedDays || {};
        const selectedSessions = selectedDays[dayOfWeek] || [];
        
        if (selectedSessions.length === 0) {
          continue; // No sessions for this day
        }
        
        // Check if slot is within any of the scheduled sessions
        const SESSION_TIMES = {
          session1: { start: { hours: 17, minutes: 0 }, end: { hours: 20, minutes: 0 } },
          session2: { start: { hours: 20, minutes: 0 }, end: { hours: 22, minutes: 0 } }
        };
        
        for (const session of selectedSessions) {
          const sessionTimes = SESSION_TIMES[session as keyof typeof SESSION_TIMES];
          if (!sessionTimes) continue;
          
          const slotHour = slot.getHours();
          const slotMinute = slot.getMinutes();
          const slotTimeInMinutes = slotHour * 60 + slotMinute;
          
          const sessionStartInMinutes = sessionTimes.start.hours * 60 + sessionTimes.start.minutes;
          const sessionEndInMinutes = sessionTimes.end.hours * 60 + sessionTimes.end.minutes;
          
          if (slotTimeInMinutes >= sessionStartInMinutes && slotTimeInMinutes < sessionEndInMinutes) {
            return true; // Slot is within scheduled availability
          }
        }
      }
    }
    
    return false; // Slot is not within any scheduled availability
  } catch (error) {
    console.error('Error checking scheduled availability:', error);
    return false;
  }
}

export async function createAppointment(data: AppointmentData) {
  try {
    // Check if the slot is within scheduled availability times
    const isWithinSchedule = await isWithinScheduledAvailability(data.coachId, data.date);
    
    if (!isWithinSchedule) {
      throw new Error('Appointments can only be booked during scheduled availability times');
    }
    
    return await addDoc(collection(db, 'appointments'), {
      ...data,
      date: Timestamp.fromDate(data.date),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error creating appointment:', error);
    throw error;
  }
}

export async function getUpcomingAppointments(userId: string, role: 'coach' | 'customer') {
  const fieldName = role === 'coach' ? 'coachId' : 'customerId';
  const baseQuery = query(
    collection(db, 'appointments'),
    where(fieldName, '==', userId),
    where('date', '>=', Timestamp.now()),
    orderBy('date', 'asc')
  );

  return executeQuery<Appointment>(baseQuery);
}

/**
 * A cancelled appointment is no longer deleted, so the upcoming list has to
 * filter it out. Absent status means visible: most existing documents carry
 * no status at all. Same rule as mobile's isVisibleAppointment (namat#36).
 */
export function isVisibleAppointment(appointment: { status?: string | null }): boolean {
  return appointment?.status !== 'cancelled';
}

/**
 * A SOFT cancel, matching mobile's cancelAppointment (namat#36 P1-7): the row
 * stays, marked cancelled, so the cancellation trigger knows who cancelled
 * (`cancelledBy`) and notifies the other party. It used to hard-delete.
 * "No known actor" is an explicit null, never ''.
 */
export async function cancelAppointment(
  appointmentId: string,
  cancelledBy: string | null
): Promise<void> {
  await updateDoc(doc(db, 'appointments', appointmentId), {
    status: 'cancelled',
    cancelledBy: cancelledBy || null,
    updatedAt: Timestamp.now(),
  });
}

export function watchUpcomingAppointments(
  userId: string,
  role: 'customer' | 'coach',
  onUpdate: (appointments: Appointment[]) => void,
  onError: (error: Error) => void
) {
  try {
    // Get start of today to include all appointments from today onwards
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startOfToday = Timestamp.fromDate(today);
    
    const appointmentsRef = collection(db, 'appointments');
    
    // Create the query with proper indexing - include all appointments from today onwards
    const q = query(
      appointmentsRef,
      where(role === 'customer' ? 'customerId' : 'coachId', '==', userId),
      where('date', '>=', startOfToday),
      orderBy('date', 'asc')
    );

    // Set up the real-time listener
    return onSnapshot(q, 
      (snapshot) => {
        const appointments = (snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Appointment[])
          .filter(isVisibleAppointment);
        onUpdate(appointments);
      },
      (error) => {
        console.error('Error in appointments listener:', error);
        onError(error);
      }
    );
  } catch (error) {
    console.error('Error setting up appointments listener:', error);
    onError(error as Error);
    return () => {}; // Return empty cleanup function
  }
}